import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

export default function SalesDataGridEnhanced({ cardName, sessionId, summaryId }) {
  const [rows, setRows] = useState([]);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    fetchSales();
  }, [cardName, sessionId]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/sales-data?card_name=${encodeURIComponent(cardName)}&search_session_id=${sessionId}`);
      const json = await res.json();
      setRows(json);
      const excluded = json.filter(row => row.is_excluded).map(row => row.id);
      setExcludedIds(new Set(excluded));
    } catch (err) {
      console.error("Failed to load sales data", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExcludeChange = async (id, isChecked) => {
    try {
      await fetch(`/api/sales-data/${id}/exclude`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_excluded: isChecked })
      });

      setExcludedIds((prev) => {
        const updated = new Set(prev);
        if (isChecked) updated.add(id);
        else updated.delete(id);
        return updated;
      });
    } catch (err) {
      console.error("Failed to update exclusion", err);
    }
  };

  const filtered = rows.filter((row) => {
    const saleDate = dayjs(row.sale_date, 'YYYY-MM-DD');
    return saleDate.isAfter(startDate) && saleDate.isBefore(dayjs(endDate).add(1, 'day'));
  });

  const includedRows = filtered.filter((row) => !excludedIds.has(row.id));
  const numericPrices = includedRows.map(r => parseFloat(r.price)).filter(p => !isNaN(p));

  const summary = numericPrices.length > 0 ? {
    count: numericPrices.length,
    low: Math.min(...numericPrices),
    high: Math.max(...numericPrices),
    avg: (numericPrices.reduce((sum, val) => sum + val, 0) / numericPrices.length).toFixed(2),
  } : { count: 0, low: 0, high: 0, avg: 0 };

  const handleRecalculate = async () => {
    try {
      const res = await fetch(`/api/recalculate-summary/${summaryId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exclude_ids: Array.from(excludedIds) }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Recalculation failed");
      alert("Summary and pricing advice successfully updated.");
    } catch (err) {
      console.error("Recalculation failed", err);
      alert("Recalculation failed.");
    }
  };

  const columns = [
    {
      field: "exclude",
      headerName: "Exclude",
      width: 90,
      renderCell: (params) => (
        <Checkbox
          checked={excludedIds.has(params.row.id)}
          onChange={(e) => handleExcludeChange(params.row.id, e.target.checked)}
        />
      ),
    },
    { field: "id", headerName: "ID", width: 70 },
    { field: "title", headerName: "Title", width: 300 },
    { field: "price", headerName: "Price", width: 100 },
    { field: "shipping_cost", headerName: "Shipping", width: 100 },
    { field: "total_price", headerName: "Total", width: 100 },
    { field: "sale_date", headerName: "Date", width: 130 },
    { field: "location", headerName: "Location", width: 150 },
    { field: "seller", headerName: "Seller", width: 150 },
    { field: "seller_feedback", headerName: "Feedback", width: 120 },
    { field: "search_term", headerName: "Search Term", width: 150 },
    { field: "search_term_exclusions", headerName: "Exclusions", width: 150 },
    {
      field: "listing_url",
      headerName: "Listing",
      width: 120,
      renderCell: (params) => (
        <a href={params.value} target="_blank" rel="noopener noreferrer">
          View
        </a>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ ml: 2 }}>
          <Typography variant="body2">
            Count: {summary.count} | Avg: ${summary.avg} | Low: ${summary.low} | High: ${summary.high}
          </Typography>
        </Box>
        <Button variant="contained" color="primary" onClick={handleRecalculate}>
          Recalculate Summary
        </Button>
      </Box>

      {loading ? (
        <CircularProgress size={24} />
      ) : includedRows.length === 0 ? (
        <Typography>No related sales found in date range.</Typography>
      ) : (
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={filtered}
            columns={columns}
            getRowId={(row) => row.id}
            pageSize={10}
            rowsPerPageOptions={[10, 25]}
          />
        </Box>
      )}
    </Box>
  );
}
